import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout, Header, Card } from '../components/mte/shell';
import {
  HERO_CELLS,
  type HeroCell,
} from '../components/mte/placeholders';
import { HeroIllustration } from '../components/mte/hero-asset-img';
import styles from './OnboardingParent.module.css';
import { apiFetch, ApiError } from '../lib/auth-fetch';

/**
 * Onboarding Parent — écran 6/11 (D-NX-069, livré 2026-09-12).
 *
 * Parcours adulte post-signup pour créer le premier profil enfant.
 * Mock : aucune persistence réelle (Vague 2 câblera PreviewAuthAdapter).
 *
 * Source spec Boussole : `04-onboarding.md §1` + `02-user-flows.md §A1 H-L`.
 *
 * Marqueurs canon :
 * - **6 héros fixes** (D-NX-038) : ninja, lobo, princesa, fada, gato, dragao
 *   - PAS de réintroduction de pégaso/pirate/sereia
 *   - PAS de classification "animaux" / inférence genre/type
 * - **Professeur-sage** (D-NX-042) = mentor universel, NON choisissable
 *   - Présent uniquement comme mention dans le récap (D-NX-042)
 * - Mission Rentrée 15×6=90 (D-NX-021b) — référencé en doc, hors écran 6
 * - QTypeSchema 11 types (D-NX-021b) — hors écran 6
 * - Espace adulte nettement distinct du monde enfant (D-NX-036 / B0)
 * - Pas de vocabulaire magique ni illustration héros hors sub-écrâ 2 (D-NX-036 / B0)
 * - Profil strict minimum : prénom + année scolaire, rien d'autre (D-NX-036 / B1)
 * - Confirmation avant choix héros : micro-question "Tu es avec ton enfant ?"
 *   (D-NX-036 / B0) — évite la décision sans l'enfant
 * - Récapitulatif cartonné par enfant à partir du 2ᵉ enfant (D-NX-036 / B0)
 * - Choix sobres : "Confirmer" plutôt que "Super choix !" (D-NX-036 / B2)
 * - D-AUTH-01 (password MVP) — EN ATTENTE Ana
 * - Sans erreur technique enfant (D-OB-03)
 *
 * État actuel : Vague 2 (MVP visuel), écran 6 navigable.
 * Sous-ecrâs : Welcome (0) → Profil (1) → Héros (2) → Récap (3).
 */

type HeroId = 'ninja' | 'lobo' | 'princesa' | 'fada' | 'gato' | 'dragao';

// Grille des héros choisissables — sage exclu (D-NX-042, non sélectionnable)
const HEROS_CHOISISSABLES: HeroCell[] = HERO_CELLS.filter(
  (cell) => cell.id !== 'sage'
);

interface ProfilEnfant {
  id: string;
  firstName: string;
  schoolLevel: 'CP' | 'CE1' | 'CE2' | 'CM1' | 'CM2';
  heroId: HeroId | null;
}

// Schéma Zod — prénom (1..24, D-OB-01) + année scolaire
const ProfilSchema = z.object({
  childFirstName: z
    .string()
    .min(1, 'Le prénom est obligatoire')
    .max(24, '24 caractères maximum (D-OB-01)'),
  schoolLevel: z.enum(['CP', 'CE1', 'CE2', 'CM1', 'CM2'], {
      errorMap: () => ({ message: 'Choisis CP, CE1, CE2, CM1 ou CM2' }),
    }),
});

type ProfilForm = z.infer<typeof ProfilSchema>;

type Step = 'welcome' | 'profile' | 'hero' | 'recap';

function OnboardingParent(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>(() => {
    return searchParams.get('mode') === 'add' ? 'profile' : 'welcome';
  });
  const [profils, setProfils] = useState<ProfilEnfant[]>([]);
  const [currentHero, setCurrentHero] = useState<HeroId | null>(null);
  const [confirmationPresence, setConfirmationPresence] = useState<
    'oui' | 'plus-tard' | null
  >(null);
  const [draftProfil, setDraftProfil] = useState<ProfilForm | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfilForm>({
    resolver: zodResolver(ProfilSchema),
    defaultValues: {
      childFirstName: '',
      schoolLevel: 'CE1',
    },
  });

  // --- Handlers ---------------------------------------------------------

  const handleStart = (): void => {
    setStep('profile');
  };

  const handleLaterWelcome = (): void => {
    navigate('/famille/accueil/');
  };

  const onSubmitProfil = (data: ProfilForm): void => {
    setDraftProfil(data);
    setCurrentHero(null);
    setConfirmationPresence(null);
    setStep('hero');
  };

  const handleBackToWelcome = (): void => {
    reset();
    setStep('welcome');
  };

  const handlePresenceChoice = (choice: 'oui' | 'plus-tard'): void => {
    setConfirmationPresence(choice);
    if (choice === 'plus-tard') {
      setCurrentHero(null);
    }
  };

  const handleConfirmHero = async (): Promise<void> => {
    if (!draftProfil || !currentHero) return;
    try {
      const result = await apiFetch<{ childId: string }>('/api/parent/children', {
        method: 'POST',
        body: JSON.stringify({
          firstName: draftProfil.childFirstName,
          schoolLevel: draftProfil.schoolLevel,
          heroId: currentHero,
        }),
      });
      const newProfil: ProfilEnfant = {
        id: result.childId,
        firstName: draftProfil.childFirstName,
        schoolLevel: draftProfil.schoolLevel,
        heroId: currentHero,
      };
      setProfils((prev) => [...prev, newProfil]);
      navigate(`/enfant/diagnostic/?childId=${encodeURIComponent(result.childId)}`);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Impossible de créer le profil.';
      window.alert('Erreur: ' + msg);
    }
  };

  const handleSkipHero = async (): Promise<void> => {
    if (!draftProfil) return;
    // 'Plus tard' means: parent defers the hero choice. Backend accepts
    // heroId=null (schema 0002_children.sql declares hero_id TEXT NULL);
    // the parent can assign the hero later via PATCH /api/parent/children/[id].
    try {
      const result = await apiFetch<{ childId: string }>('/api/parent/children', {
        method: 'POST',
        body: JSON.stringify({
          firstName: draftProfil.childFirstName,
          schoolLevel: draftProfil.schoolLevel,
          heroId: null,
        }),
      });
      const newProfil: ProfilEnfant = {
        id: result.childId,
        firstName: draftProfil.childFirstName,
        schoolLevel: draftProfil.schoolLevel,
        heroId: null,
      };
      setProfils((prev) => [...prev, newProfil]);
      navigate(`/enfant/diagnostic/?childId=${encodeURIComponent(result.childId)}`);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Impossible de créer le profil.';
      window.alert('Erreur: ' + msg);
    }
  };

  const handleAddAnother = (): void => {
    setDraftProfil(null);
    setCurrentHero(null);
    setConfirmationPresence(null);
    reset({ childFirstName: '', schoolLevel: 'CE1' });
    setStep('profile');
  };

  const handleViewProfile = (): void => {
    // Route suivante (first-time flow Ana 18/09 ~18:45 : profile → hero → diagnostic) : /enfant/diagnostic/
    navigate('/enfant/diagnostic/');
  };

  // --- Rendu ------------------------------------------------------------

  return (
    <Layout
      context="adulte"
      width="narrow"
      header={
        <Header
          context="adulte"
          logo={
            <>
              <span className={styles.brandMark} aria-hidden="true">
                ✦
              </span>
              <span className={styles.brandText}>Mon Temps d'Étude</span>
            </>
          }
        />
      }
      footer={
        <footer className={styles.footer}>
          <p>
            <Link to="/famille/accueil/">← Quitter l'onboarding</Link>
          </p>
          <p className={styles.footerMeta}>
            Vague 2 · D-NX-069 · écran 6/11 — build 2026-09-12
          </p>
        </footer>
      }
    >
      <main className={styles.main} aria-labelledby="onboarding-title">
        {step === 'welcome' && (
          <section
            className={styles.welcome}
            aria-labelledby="onboarding-title"
          >
            <p className={styles.eyebrow}>Onboarding parent</p>
            <h1 id="onboarding-title" className={styles.title}>
              Bienvenue&nbsp;!
            </h1>
            <p className={styles.lede}>
              En 3 étapes, ton enfant commence l'aventure.
            </p>
            <ol className={styles.stepsList}>
              <li>
                <strong>1. Le profil</strong> — prénom et année scolaire de
                ton enfant.
              </li>
              <li>
                <strong>2. Le héros</strong> — son compagnon dans le monde
                (optionnel, il peut choisir plus tard).
              </li>
              <li>
                <strong>3. Le récapitulatif</strong> — tout est prêt, on
                démarre quand tu veux.
              </li>
            </ol>
            <div className={styles.actions} role="group" aria-label="Actions">
              <button
                type="button"
                className={styles.primaryAction}
                onClick={handleStart}
                data-action="commencer"
              >
                Commencer
              </button>
              <button
                type="button"
                className={styles.secondaryAction}
                onClick={handleLaterWelcome}
                data-action="plus-tard"
              >
                Plus tard
              </button>
            </div>
          </section>
        )}

        {step === 'profile' && (
          <section className={styles.profile} aria-labelledby="profile-title">
            <p className={styles.eyebrow}>Étape 1 sur 3</p>
            <h1 id="profile-title" className={styles.title}>
              Le profil de ton enfant.
            </h1>
            <p className={styles.lede}>
              Strict minimum&nbsp;: prénom et année scolaire. Les autres infos
              (école, photo, etc.) restent possibles plus tard, depuis
              l'espace parent.
            </p>

            <Card elevation="md" childSized={false} className={styles.formCard}>
              <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmitProfil)}
                noValidate
              >
                <div className={styles.field}>
                  <label htmlFor="childFirstName" className={styles.label}>
                    Prénom
                  </label>
                  <input
                    id="childFirstName"
                    type="text"
                    autoComplete="given-name"
                    className={styles.input}
                    aria-invalid={!!errors.childFirstName}
                    aria-describedby={
                      errors.childFirstName
                        ? 'childFirstName-error'
                        : undefined
                    }
                    {...register('childFirstName')}
                  />
                  {errors.childFirstName && (
                    <p
                      id="childFirstName-error"
                      className={styles.errorMsg}
                      role="alert"
                    >
                      {errors.childFirstName.message}
                    </p>
                  )}
                </div>

                <fieldset className={styles.fieldset}>
                  <legend className={styles.legend}>Année scolaire</legend>
                  <div
                    className={styles.yearRow}
                    role="radiogroup"
                    aria-label="Année scolaire"
                  >
                    <label className={styles.yearChoice}>
                      <input
                        type="radio"
                        value="CE1"
                        className={styles.yearRadio}
                        aria-invalid={!!errors.schoolLevel}
                        {...register('schoolLevel')}
                      />
                      <span className={styles.yearLabel}>CE1</span>
                      <span className={styles.yearHint}>
                        Cours élémentaire 1
                      </span>
                    </label>
                    <label className={styles.yearChoice}>
                      <input
                        type="radio"
                        value="CM1"
                        className={styles.yearRadio}
                        aria-invalid={!!errors.schoolLevel}
                        {...register('schoolLevel')}
                      />
                      <span className={styles.yearLabel}>CM1</span>
                      <span className={styles.yearHint}>
                        Cours moyen 1
                      </span>
                    </label>
                  </div>
                  {errors.schoolLevel && (
                    <p className={styles.errorMsg} role="alert">
                      {errors.schoolLevel.message}
                    </p>
                  )}
                </fieldset>

                <div className={styles.actions} role="group" aria-label="Actions">
                  <button
                    type="button"
                    className={styles.secondaryAction}
                    onClick={handleBackToWelcome}
                    data-action="retour"
                  >
                    ← Retour
                  </button>
                  <button
                    type="submit"
                    className={styles.primaryAction}
                    data-action="continuer"
                  >
                    Continuer →
                  </button>
                </div>
              </form>
            </Card>
          </section>
        )}

        {step === 'hero' && draftProfil && (
          <section className={styles.hero} aria-labelledby="hero-title">
            <p className={styles.eyebrow}>Étape 2 sur 3</p>
            <h1 id="hero-title" className={styles.title}>
              {`Choisis le héros de ${draftProfil.childFirstName}.`}
            </h1>

            {confirmationPresence === null && (
              <div className={styles.presencePrompt}>
                <p className={styles.lede}>
                  Tu es avec ton enfant&nbsp;?
                </p>
                <div
                  className={styles.actions}
                  role="group"
                  aria-label="Présence de l'enfant"
                >
                  <button
                    type="button"
                    className={styles.primaryAction}
                    onClick={() => handlePresenceChoice('oui')}
                    data-action="avec-enfant"
                  >
                    Oui, on choisit ensemble
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryAction}
                    onClick={() => handlePresenceChoice('plus-tard')}
                    data-action="plus-tard"
                  >
                    Plus tard, je reviendrai
                  </button>
                </div>
              </div>
            )}

            {confirmationPresence === 'oui' && (
              <>
                <p className={styles.lede}>
                  Une petite décision qui ressemble à{' '}
                  {draftProfil.childFirstName}.
                </p>
                <section
                  className={styles.heroGrid}
                  aria-label="Choisis le héros"
                  role="radiogroup"
                >
                  {HEROS_CHOISISSABLES.map((cell) => {
                    const isSelected = currentHero === cell.id;
                    return (
                      <Card
                        key={cell.id}
                        elevation={isSelected ? 'lg' : 'md'}
                        childSized={true}
                        className={
                          isSelected
                            ? styles.heroCardSelected
                            : styles.heroCard
                        }
                      >
                        <button
                          type="button"
                          onClick={() => setCurrentHero(cell.id as HeroId)}
                          className={styles.heroButton}
                          role="radio"
                          aria-checked={isSelected}
                          aria-label={`${cell.label}, ${cell.role}`}
                          data-hero-id={cell.id}
                        >
                          <HeroIllustration cell={cell} />
                          <figcaption className={styles.heroCaption}>
                            <span className={styles.heroLabel}>
                              {cell.label}
                            </span>
                            <span className={styles.heroRole}>
                              {cell.role}
                            </span>
                          </figcaption>
                          {isSelected && (
                            <span
                              className={styles.heroCheck}
                              aria-hidden="true"
                            >
                              ✓
                            </span>
                          )}
                        </button>
                      </Card>
                    );
                  })}
                </section>

                <div
                  className={styles.actions}
                  role="group"
                  aria-label="Actions héros"
                >
                  <button
                    type="button"
                    className={styles.secondaryAction}
                    onClick={handleSkipHero}
                    data-action="skip-hero"
                  >
                    Plus tard
                  </button>
                  <button
                    type="button"
                    className={styles.primaryAction}
                    onClick={handleConfirmHero}
                    disabled={!currentHero}
                    data-action="confirmer"
                  >
                    {currentHero ? 'Confirmer' : 'Choisis un héros'}
                  </button>
                </div>
              </>
            )}

            {confirmationPresence === 'plus-tard' && (
              <div className={styles.actions} role="group" aria-label="Actions">
                <button
                  type="button"
                  className={styles.primaryAction}
                  onClick={handleSkipHero}
                  data-action="continuer-sans-hero"
                >
                  Continuer sans héros →
                </button>
              </div>
            )}
          </section>
        )}

        {step === 'recap' && (
          <section className={styles.recap} aria-labelledby="recap-title">
            <p className={styles.eyebrow}>Étape 3 sur 3</p>
            <h1 id="recap-title" className={styles.title}>
              Tout est prêt&nbsp;!
            </h1>

            {profils.length === 1 && profils[0] && (
              <p className={styles.lede}>
                {profils[0].firstName}
                {profils[0].heroId
                  ? ` et ${
                      HEROS_CHOISISSABLES.find(
                        (c) => c.id === profils[0]!.heroId
                      )?.label ?? 'son héros'
                    }`
                  : ''}{' '}
                vont commencer leur aventure. Donne-lui le téléphone quand il ou
                elle est prêt(e).
              </p>
            )}

            {profils.length > 1 && (
              <p className={styles.lede}>
                {profils.length} profils enfants prêts. Donne-leur le
                téléphone quand ils ou elles sont prêt(e)s.
              </p>
            )}

            <ul className={styles.cartouches} aria-label="Profils créés">
              {profils.map((p) => (
                <li key={p.id} className={styles.cartouche} data-profil-id={p.id}>
                  <div className={styles.cartoucheHeader}>
                    <span className={styles.cartoucheName}>{p.firstName}</span>
                    <span className={styles.cartoucheYear}>
                      {p.schoolLevel}
                    </span>
                  </div>
                  <div className={styles.cartoucheHero}>
                    {p.heroId
                      ? `${
                          HEROS_CHOISISSABLES.find((c) => c.id === p.heroId)
                            ?.label ?? '?'
                        }`
                      : '— Pas encore de héros'}
                  </div>
                  <div className={styles.cartoucheMentor}>
                    Mentor&nbsp;: le professeur-sage (universel).
                  </div>
                </li>
              ))}
            </ul>

            <div className={styles.actions} role="group" aria-label="Actions">
              <button
                type="button"
                className={styles.secondaryAction}
                onClick={handleAddAnother}
                data-action="ajouter-enfant"
              >
                + Ajouter un autre enfant
              </button>
              <button
                type="button"
                className={styles.primaryAction}
                onClick={handleViewProfile}
                data-action="voir-profil"
              >
                Voir le profil →
              </button>
            </div>
          </section>
        )}
      </main>
    </Layout>
  );
}

export default OnboardingParent;